import React, { useState } from 'react'
import { ProjectFormField } from '@/services/templateApi'
import { useProjectForm } from '@/hooks/useProjectForm'
import { CustomBeverageStyleManager } from './CustomBeverageStyleManager'

interface TemplateDrivenProjectFormProps {
  formData: Record<string, any>
  onFormChange: (field: string, value: any) => void
  className?: string
}

export const TemplateDrivenProjectForm: React.FC<TemplateDrivenProjectFormProps> = ({
  formData,
  onFormChange,
  className = ''
}) => {
  const [showBeverageStyleManager, setShowBeverageStyleManager] = useState(false)
  const {
    projectFormConfig,
    loading,
    error,
    currentTemplate,
    getFieldsBySection,
    getSections
  } = useProjectForm()

  // Debug logging
  console.log('🎨 TemplateDrivenProjectForm render:', {
    loading,
    error,
    hasTemplate: !!currentTemplate,
    templateBusinessType: currentTemplate?.businessType,
    hasProjectFormConfig: !!projectFormConfig,
    projectFieldsCount: projectFormConfig?.projectFields?.length || 0,
    projectFields: projectFormConfig?.projectFields?.map(f => f.name) || []
  })

  // Get business type specific title
  const getFormTitle = () => {
    if (!currentTemplate) return 'Project Details'
    
    switch (currentTemplate.businessType) {
      case 'home_brewing':
        return 'Recipe Details'
      case 'culinary':
        return 'Recipe Details'
      case '3d_printing':
        return 'Print Job Details'
      case 'photography':
        return 'Session Details'
      case 'jewelry':
        return 'Design Details'
      case 'woodworking':
        return 'Build Details'
      case 'automotive':
        return 'Project Details'
      case 'electronics':
        return 'Circuit Details'
      default:
        return 'Project Details'
    }
  }

  if (loading) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jewelry-primary"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading project form...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    )
  }

  if (!projectFormConfig || !projectFormConfig.projectFields.length) {
    // Show a message if we have a template but no project fields configured
    if (currentTemplate && !loading && !error) {
      return (
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {getFormTitle()}
          </h2>
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>No additional project fields configured for this template.</p>
            <p className="text-sm mt-2">Template: {currentTemplate.name}</p>
          </div>
        </div>
      )
    }
    
    // If no template or still loading, don't render anything
    return null
  }

  const renderField = (field: ProjectFormField) => {
    const value = formData[field.name] || ''

    const baseInputClasses = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => onFormChange(field.name, e.target.value)}
            className={baseInputClasses}
            placeholder={field.placeholder}
            required={field.required}
            min={field.min}
            max={field.max}
            step={field.step}
          />
        )

      case 'number':
        return (
          <div className="relative">
            <input
              type="number"
              id={field.name}
              name={field.name}
              value={value}
              onChange={(e) => onFormChange(field.name, parseFloat(e.target.value) || '')}
              className={baseInputClasses}
              placeholder={field.placeholder}
              required={field.required}
              min={field.min}
              max={field.max}
              step={field.step}
            />
            {field.unit && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 dark:text-gray-400 text-sm">{field.unit}</span>
              </div>
            )}
          </div>
        )

      case 'range':
        return (
          <div className="space-y-2">
            <input
              type="range"
              id={field.name}
              name={field.name}
              value={value}
              onChange={(e) => onFormChange(field.name, parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              min={field.min}
              max={field.max}
              step={field.step}
              required={field.required}
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>{field.min}{field.unit && ` ${field.unit}`}</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {value}{field.unit && ` ${field.unit}`}
              </span>
              <span>{field.max}{field.unit && ` ${field.unit}`}</span>
            </div>
          </div>
        )

      case 'select':
        // Special handling for beverage_style field
        if (field.name === 'beverage_style' && currentTemplate?.businessType === 'home_brewing') {
          return (
            <div className="space-y-3">
              <select
                id={field.name}
                name={field.name}
                value={value}
                onChange={(e) => onFormChange(field.name, e.target.value)}
                className={baseInputClasses}
                required={field.required}
              >
                <option value="">{field.placeholder || `Select ${field.label}`}</option>
                {field.options?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              
              {/* Add button to manage custom styles */}
              <button
                type="button"
                onClick={() => setShowBeverageStyleManager(!showBeverageStyleManager)}
                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              >
                {showBeverageStyleManager ? 'Hide' : 'Add'} Custom Beverage Styles
              </button>
              
              {/* Custom beverage style manager */}
              {showBeverageStyleManager && (
                <div className="mt-4 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <CustomBeverageStyleManager
                    onStyleAdded={(style) => {
                      // Refresh the form to include the new style
                      console.log('New beverage style added:', style)
                      // The form will automatically refresh when the user reloads or the API is called again
                    }}
                    onStyleUpdated={(style) => {
                      console.log('Beverage style updated:', style)
                    }}
                    onStyleDeleted={(styleId) => {
                      console.log('Beverage style deleted:', styleId)
                      // If the deleted style was selected, clear the selection
                      if (formData[field.name] && formData[field.name] === styleId.toString()) {
                        onFormChange(field.name, '')
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )
        }
        
        // Default select rendering
        return (
          <select
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => onFormChange(field.name, e.target.value)}
            className={baseInputClasses}
            required={field.required}
          >
            <option value="">{field.placeholder || `Select ${field.label}`}</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )

      case 'multiselect':
        return (
          <select
            id={field.name}
            name={field.name}
            value={Array.isArray(value) ? value : []}
            onChange={(e) => {
              const selectedOptions = Array.from(e.target.selectedOptions, option => option.value)
              onFormChange(field.name, selectedOptions)
            }}
            className={baseInputClasses}
            multiple
            required={field.required}
          >
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )

      case 'textarea':
        return (
          <textarea
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => onFormChange(field.name, e.target.value)}
            className={baseInputClasses}
            placeholder={field.placeholder}
            required={field.required}
            rows={4}
          />
        )

      case 'date':
        return (
          <input
            type="date"
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => onFormChange(field.name, e.target.value)}
            className={baseInputClasses}
            required={field.required}
          />
        )

      case 'time':
        return (
          <input
            type="time"
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => onFormChange(field.name, e.target.value)}
            className={baseInputClasses}
            required={field.required}
          />
        )

      default:
        return (
          <input
            type="text"
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => onFormChange(field.name, e.target.value)}
            className={baseInputClasses}
            placeholder={field.placeholder}
            required={field.required}
          />
        )
    }
  }

  const sections = getSections()
  const fieldsWithoutSection = getFieldsBySection()

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
        {getFormTitle()}
      </h2>

      {/* Template Information */}
      {currentTemplate && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
            Template Information
          </h4>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            This form is customized for <strong>{currentTemplate.name}</strong> business template.
            Fields are tailored for {currentTemplate.industry} industry standards.
          </p>
        </div>
      )}

      {/* Fields without sections first */}
      {fieldsWithoutSection.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {fieldsWithoutSection.map((field) => (
            <div key={field.name}>
              <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {renderField(field)}
              {field.description && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {field.description}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Sectioned fields */}
      {sections.map((section) => {
        const sectionFields = getFieldsBySection(section)
        if (sectionFields.length === 0) return null

        return (
          <div key={section} className="mb-8">
            <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
              {section}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {sectionFields.map((field) => (
                <div key={field.name} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                  <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                  {field.description && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {field.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      })}

      {/* Show message if no fields are configured */}
      {!fieldsWithoutSection.length && !sections.length && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No additional project fields configured for this template.</p>
        </div>
      )}
    </div>
  )
}